/**
 * List Parser
 * Handles detection and conversion of Microsoft Word lists to proper HTML lists
 *
 * Word outputs lists as paragraphs with special classes and styles:
 * - MsoListParagraph, MsoListParagraphCxSpFirst/Middle/Last
 * - style="mso-list: l0 level1 lfo1"
 * - Bullets/numbers in conditional comments: <!--[if !supportLists]-->•<!--[endif]-->
 */

/**
 * Represents a parsed Word list item
 */
export interface WordListItem {
  /** The text content of the list item (without bullet/number) */
  text: string;
  /** The nesting level (1-based, where 1 is top level) */
  level: number;
  /** Whether this is an ordered (numbered) list */
  isOrdered: boolean;
  /** The list identifier from Word (used to group related items) */
  listId: string;
  /** The original HTML content for reference */
  originalHtml: string;
}

/**
 * Detects whether an element represents an ordered list item
 * by examining the bullet/number character in conditional comments
 */
function detectOrderedList(html: string): boolean {
  // Extract content from conditional comments
  const conditionalMatch = html.match(
    /<!--\[if !supportLists\]-->([\s\S]*?)<!--\[endif\]-->/i
  );

  if (conditionalMatch) {
    const bulletContent = conditionalMatch[1];

    // Check for numbered patterns: 1., 1), (1), a., a), i., ii., etc.
    const numberedPatterns = [
      /\d+[.)]/,           // 1. or 1)
      /\(\d+\)/,           // (1)
      /[a-z][.)]/i,        // a. or a) or A. or A)
      /\([a-z]\)/i,        // (a) or (A)
      /^[ivxlcdm]+[.)]/i,  // Roman numerals: i. ii. iii. iv.
      /\([ivxlcdm]+\)/i,   // (i) (ii) (iii)
    ];

    for (const pattern of numberedPatterns) {
      if (pattern.test(bulletContent)) {
        return true;
      }
    }
  }

  // Check for bullet indicators (unordered)
  const bulletIndicators = [
    /font-family:\s*Symbol/i,
    /font-family:\s*Wingdings/i,
    /font-family:\s*["']?Courier New["']?/i,
    /[•·\-*◦▪■□●○►▸‣⁃]/,
  ];

  for (const indicator of bulletIndicators) {
    if (indicator.test(html)) {
      return false; // It's a bullet (unordered)
    }
  }

  // Default to unordered if we can't determine
  return false;
}

/**
 * Extracts clean text content from a Word list item,
 * removing the bullet/number and conditional comments
 */
function extractListItemText(html: string): string {
  let text = html;

  // Remove conditional comments and their content (contains bullet/number)
  text = text.replace(
    /<!--\[if !supportLists\]-->[\s\S]*?<!--\[endif\]-->/gi,
    ""
  );

  // Remove any remaining HTML comments
  text = text.replace(/<!--[\s\S]*?-->/g, "");

  // Remove the outer paragraph tags
  text = text.replace(/<p[^>]*>/gi, "");
  text = text.replace(/<\/p>/gi, "");

  // Remove span tags but keep their content
  text = text.replace(/<span[^>]*>/gi, "");
  text = text.replace(/<\/span>/gi, "");

  // Remove <o:p> tags (Office paragraph markers)
  text = text.replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, "");

  // Remove non-breaking spaces that Word adds
  text = text.replace(/&nbsp;/gi, " ");
  text = text.replace(/\u00A0/g, " ");

  // Clean up whitespace
  text = text.replace(/\s+/g, " ").trim();

  return text;
}

/**
 * Parses the mso-list style to extract list ID and level
 * Format: mso-list: l0 level1 lfo1
 */
function parseMsoListStyle(style: string): { listId: string; level: number } | null {
  const match = style.match(/mso-list:\s*l(\d+)\s+level(\d+)/i);

  if (match) {
    return {
      listId: match[1],
      level: parseInt(match[2], 10),
    };
  }

  return null;
}

/**
 * Detects and extracts all Word list items from HTML
 * @param html - HTML string potentially containing Word list items
 * @returns Array of parsed list items
 */
export function detectWordListItems(html: string): WordListItem[] {
  const items: WordListItem[] = [];

  // Match paragraphs that are list items
  // Word uses classes like MsoListParagraph, MsoListParagraphCxSpFirst, etc.
  const listParagraphRegex =
    /<p[^>]*(?:class="[^"]*MsoListParagraph[^"]*"|style="[^"]*mso-list:[^"]*")[^>]*>[\s\S]*?<\/p>/gi;

  let match;
  while ((match = listParagraphRegex.exec(html)) !== null) {
    const paragraphHtml = match[0];

    // Extract style attribute
    const styleMatch = paragraphHtml.match(/style="([^"]*)"/i);
    const style = styleMatch ? styleMatch[1] : "";

    // Parse mso-list to get level and list ID
    const listInfo = parseMsoListStyle(style);

    if (listInfo) {
      const text = extractListItemText(paragraphHtml);
      const isOrdered = detectOrderedList(paragraphHtml);

      // Only add if we have actual text content
      if (text) {
        items.push({
          text,
          level: listInfo.level,
          isOrdered,
          listId: listInfo.listId,
          originalHtml: paragraphHtml,
        });
      }
    }
  }

  return items;
}

/**
 * Builds proper HTML list structure from parsed Word list items
 * Handles nested lists and mixed ordered/unordered lists
 */
export function buildListStructure(items: WordListItem[]): string {
  if (items.length === 0) return "";

  const result: string[] = [];
  const listStack: Array<{ type: "ul" | "ol"; level: number }> = [];

  items.forEach((item) => {
    const listType = item.isOrdered ? "ol" : "ul";
    const currentLevel = item.level;

    // Determine current stack level
    const stackLevel = listStack.length > 0 ? listStack[listStack.length - 1].level : 0;

    if (listStack.length === 0) {
      // Start first list
      result.push(`<${listType}>`);
      listStack.push({ type: listType, level: currentLevel });
    } else if (currentLevel > stackLevel) {
      // Need to nest deeper - create new nested list(s)
      while (listStack.length > 0 && listStack[listStack.length - 1].level < currentLevel) {
        // Start a new nested list inside the current item
        result.push(`<${listType}>`);
        listStack.push({ type: listType, level: listStack[listStack.length - 1].level + 1 });
      }
    } else if (currentLevel < stackLevel) {
      // Need to close nested lists until we reach the right level
      while (listStack.length > 0 && listStack[listStack.length - 1].level > currentLevel) {
        const popped = listStack.pop()!;
        result.push(`</${popped.type}></li>`);
      }

      // Check if we need to switch list types at the same level
      if (listStack.length > 0 && listStack[listStack.length - 1].type !== listType) {
        const popped = listStack.pop()!;
        result.push(`</${popped.type}>`);
        result.push(`<${listType}>`);
        listStack.push({ type: listType, level: currentLevel });
      }
    } else {
      // Same level - check if list type changed
      if (listStack.length > 0 && listStack[listStack.length - 1].type !== listType) {
        const popped = listStack.pop()!;
        result.push(`</${popped.type}>`);
        result.push(`<${listType}>`);
        listStack.push({ type: listType, level: currentLevel });
      }
    }

    // Check if next item is nested (need to keep li open)
    const nextItemIndex = items.indexOf(item) + 1;
    const nextItem = items[nextItemIndex];
    const isNextNested = nextItem && nextItem.level > currentLevel;

    if (isNextNested) {
      // Don't close the li yet - nested list will go inside
      result.push(`<li>${item.text}`);
    } else {
      result.push(`<li>${item.text}</li>`);
    }
  });

  // Close all remaining open lists
  while (listStack.length > 0) {
    const popped = listStack.pop()!;
    result.push(`</${popped.type}>`);
  }

  return result.join("");
}

/**
 * Converts Word list paragraphs in HTML to proper HTML lists
 * @param html - Original HTML with Word list paragraphs
 * @param items - Parsed list items from detectWordListItems
 * @returns HTML with proper list structure
 */
export function convertWordListsToHtml(
  html: string,
  items: WordListItem[]
): string {
  if (items.length === 0) return html;

  // Group consecutive list items
  const listGroups: WordListItem[][] = [];
  let currentGroup: WordListItem[] = [];

  items.forEach((item, index) => {
    currentGroup.push(item);

    // Check if next item is not consecutive in the original HTML
    // or if this is the last item
    const nextItem = items[index + 1];
    if (!nextItem) {
      listGroups.push([...currentGroup]);
      currentGroup = [];
    }
  });

  // Replace each group with proper list HTML
  let result = html;
  listGroups.forEach((group) => {
    if (group.length === 0) return;

    // Build the proper list HTML
    const listHtml = buildListStructure(group);

    // Replace the original Word paragraphs with the new list
    // We need to find and replace all paragraphs in this group
    group.forEach((item, index) => {
      if (index === 0) {
        // Replace first item with the complete list
        result = result.replace(item.originalHtml, listHtml);
      } else {
        // Remove subsequent items (they're now part of the list)
        result = result.replace(item.originalHtml, "");
      }
    });
  });

  return result;
}

/**
 * Quick check if HTML contains Word list patterns
 */
export function containsWordLists(html: string): boolean {
  return (
    /class="[^"]*MsoListParagraph/i.test(html) ||
    /style="[^"]*mso-list:/i.test(html)
  );
}
